import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';

import { useQueryClient } from '@tanstack/react-query';
import { getRouteApi, useLoaderData } from '@tanstack/react-router';

import {
  type FloorPlans,
  getMediaOptions,
  type ProjectLayoutType,
  useMedia,
  useProjectMedia,
} from '@/api/media';
import { useDeleteFloorLayouts } from '@/features/estate-media/api/delete-floor-layouts';
import { useUploadFloorLayouts } from '@/features/estate-media/api/upload-floor-layout';
import { useEstate } from '@/features/estates-property/api/estate';
import { canWrite } from '@/features/permissions/resource-permissions';

import { ACCEPTED_FLOORPLANS_INPUT_TYPE, uploadLayoutsSchema } from '../schema/upload-media';
import { MediaData } from './media-data';
import { UploadMediaModal, type UploadMediaModalType } from './upload-media-modal';
import { useModal } from '../../../components/modals/new/use-modal';
import { ConfirmationModal } from '../../../components/modals/confirmation-modal';

const route = getRouteApi(
  '/_authenticated/_sidebar-layout/estates/$estateId/_dashboard-layout/media/floor-plans'
);

export const FloorPlansDashboard = () => {
  const { estateId } = route.useParams();
  const { openUpload } = route.useSearch();
  const navigate = route.useNavigate();
  const { data } = useMedia({ id: estateId });
  const { data: estate } = useEstate({ id: estateId });
  const { data: projectMedia } = useProjectMedia({ id: estate?.projectId, forEstate: true });
  const estateDashboard = useLoaderData({
    from: '/_authenticated/_sidebar-layout/estates/$estateId/_dashboard-layout',
  });
  const canWriteMedia = canWrite(estateDashboard.metadata?.permissions, 'media');

  const uploadModal = useModal();
  const deleteModal = useModal();
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [deleteCallback, setDeleteCallback] = useState<() => void>(() => () => {});

  const dataFloorLayouts = useMemo(() => {
    const dates = new Set<string>([
      ...Object.keys(data.floorLayouts),
      ...Object.keys(projectMedia.layouts),
    ]);
    const groupedImages = Array.from(dates).reduce<
      Record<string, FloorPlans[string] | ProjectLayoutType[]>
    >((acc, date) => {
      const estateFloorLayouts = data.floorLayouts[date] || [];
      const projectFloorLayouts = projectMedia.layouts[date] || [];
      acc[date] = [...estateFloorLayouts, ...projectFloorLayouts];
      return acc;
    }, {});
    return groupedImages;
  }, [data, projectMedia]);

  const { mutateAsync: uploadFloorLayouts } = useUploadFloorLayouts({
    mutationConfig: {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: getMediaOptions(estateId).queryKey });
        toast.success(t('floor-plan-uploaded-success'));
      },
    },
  });

  const { mutate: deleteFloorLayouts } = useDeleteFloorLayouts({
    mutationConfig: {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: getMediaOptions(estateId).queryKey });
        toast.success(t('floor-plan-deleted-success'));
      },
    },
  });

  const submitHandler = useCallback(
    async ({ redirect, ...data }: UploadMediaModalType) => {
      // Convert File[] to FileList
      const dataTransfer = new DataTransfer();
      data.files.forEach((file) => dataTransfer.items.add(file));
      await uploadFloorLayouts({ layouts: dataTransfer.files, estateId });
      uploadModal.dismiss();
      if (redirect) {
        // TODO: re-direct User to Editor
      }
    },
    [uploadFloorLayouts, estateId, uploadModal.dismiss]
  );

  const handleOpenModal = useCallback(() => {
    uploadModal.open();
  }, [uploadModal]);

  const { t } = useTranslation();

  const queryClient = useQueryClient();

  const handleDelele = (selectedIds: string[], callback: () => void) => {
    setSelectedIds(selectedIds);
    setDeleteCallback(() => callback);
    deleteModal.open();
  };

  // biome-ignore lint/correctness/useExhaustiveDependencies: ignored
  useEffect(() => {
    if (!openUpload) return;
    handleOpenModal();
    // Remove search param: openModal
    navigate({
      search: (prev) => ({
        ...prev,
        openUpload: undefined,
      }),
    });
  }, [handleOpenModal, openUpload]);

  return (
    <>
      <MediaData
        data={dataFloorLayouts}
        onUpload={handleOpenModal}
        handleDelete={handleDelele}
        canWriteMedia={canWriteMedia}
        page="floor-plans"
        type="estate"
        estateId={estateId}
      />
      <UploadMediaModal
        modal={uploadModal}
        submitHandler={submitHandler}
        acceptedType={ACCEPTED_FLOORPLANS_INPUT_TYPE}
        acceptedMediaSchema={uploadLayoutsSchema()}
      />
      <ConfirmationModal
        modal={deleteModal}
        title={t('deleteSelectedTitle', { count: selectedIds.length })}
        description={t('deleteSelectedDescription')}
        confirmationText={t('delete')}
        destructive
        onSubmit={() =>
          deleteFloorLayouts({ estateId, layoutIds: selectedIds }, { onSuccess: deleteCallback })
        }
      />
    </>
  );
};
